import type { LoadedCustomComponentModule } from './hostRuntime';

/**
 * Stateful compiled-artifact loader shared by `CustomComponentProvider` (in the React tree) and
 * Vike's `+onBeforeRenderClient` (before the tree mounts). Owns the module / blob URL caches that
 * dedupe across slugs and re-entrancy.
 */
export interface CustomComponentLoaderManifest {
  slugToCurrentSourceHash: Record<string, string>;
}

export interface CustomComponentLoaderOptions {
  resolveCompiledUrl: (sourceHash: string) => string;
  resolveRuntimeUrl: (specifier: string) => string;
  // Getter so the editor's mutable manifest stays live without rebuilding the loader.
  getManifest: () => CustomComponentLoaderManifest;
}

export interface CustomComponentLoader {
  loadByHash: (sourceHash: string) => Promise<LoadedCustomComponentModule>;
  loadBySlug: (slug: string) => Promise<LoadedCustomComponentModule>;
  /** Sync lookup for hashes whose promise has already resolved; returns `null` otherwise. */
  getResolvedByHash: (sourceHash: string) => LoadedCustomComponentModule | null;
  // Blob URL → sourceHash, used by the provider's window-error attribution.
  blobUrlToSourceHash: ReadonlyMap<string, string>;
}

const COMPONENT_IMPORT_PATTERN = /^components\/([a-z0-9][a-z0-9-]*)$/;
const RUNTIME_PATH_PREFIX = '/__mutiny/runtime/';

export function createCustomComponentLoader(options: CustomComponentLoaderOptions): CustomComponentLoader {
  const moduleCache = new Map<string, Promise<LoadedCustomComponentModule>>();
  const resolvedCache = new Map<string, LoadedCustomComponentModule>();
  const blobUrlCache = new Map<string, Promise<string>>();
  const blobUrlToSourceHash = new Map<string, string>();
  const inFlight = new Set<string>();

  async function loadByHash(sourceHash: string): Promise<LoadedCustomComponentModule> {
    const cached = moduleCache.get(sourceHash);
    if (cached) return cached;
    const promise = (async () => {
      const blobUrl = await getBlobUrl(sourceHash);
      const mod = (await import(/* @vite-ignore */ /* webpackIgnore: true */ blobUrl)) as LoadedCustomComponentModule;
      resolvedCache.set(sourceHash, mod);
      return mod;
    })();
    moduleCache.set(sourceHash, promise);
    promise.catch(() => {
      if (moduleCache.get(sourceHash) === promise) moduleCache.delete(sourceHash);
    });
    return promise;
  }

  async function loadBySlug(slug: string): Promise<LoadedCustomComponentModule> {
    const manifest = options.getManifest();
    const sourceHash = manifest.slugToCurrentSourceHash[slug];
    if (!sourceHash) {
      throw new Error(`Custom component slug "${slug}" is not registered for this page.`);
    }
    return loadByHash(sourceHash);
  }

  async function getBlobUrl(sourceHash: string): Promise<string> {
    const cached = blobUrlCache.get(sourceHash);
    if (cached) return cached;
    if (inFlight.has(sourceHash)) {
      throw new Error(`Circular custom-component import detected at sourceHash ${sourceHash}.`);
    }
    inFlight.add(sourceHash);
    const promise = (async () => {
      const text = await fetchArtifactText(options.resolveCompiledUrl(sourceHash));
      const rewritten = await rewriteBareSpecifiers(text);
      const url = URL.createObjectURL(new Blob([rewritten], { type: 'text/javascript' }));
      blobUrlToSourceHash.set(url, sourceHash);
      return url;
    })();
    blobUrlCache.set(sourceHash, promise);
    promise
      .catch(() => {
        if (blobUrlCache.get(sourceHash) === promise) blobUrlCache.delete(sourceHash);
      })
      .finally(() => {
        inFlight.delete(sourceHash);
      });
    return promise;
  }

  async function rewriteBareSpecifiers(text: string): Promise<string> {
    const regex = /(\bfrom\s*['"])([^'"\n]+)(['"])/g;
    const candidates: Array<{ matchText: string; spec: string }> = [];
    for (const m of text.matchAll(regex)) {
      const matchText = m[0];
      const spec = m[2];
      if (matchText && spec) candidates.push({ matchText, spec });
    }
    // Resolve in parallel — each resolution either hits the per-sourceHash cache or recursively
    // builds a child blob URL. Since the cache promise is set before recursion, concurrent calls
    // for the same slug share one resolution.
    const resolutions = await Promise.all(
      candidates.map(async ({ matchText, spec }) => {
        const replacement = await resolveSpecifier(spec);
        return replacement && replacement !== spec ? { matchText, spec, replacement } : null;
      }),
    );
    // Apply replacements after resolution so we don't re-match rewritten URLs.
    return resolutions.reduce<string>((acc, r) => {
      if (!r) return acc;
      return acc.replace(r.matchText, r.matchText.replace(r.spec, r.replacement));
    }, text);
  }

  async function resolveSpecifier(spec: string): Promise<string | null> {
    if (spec.startsWith(RUNTIME_PATH_PREFIX)) {
      return options.resolveRuntimeUrl(spec);
    }
    const componentMatch = COMPONENT_IMPORT_PATTERN.exec(spec);
    if (componentMatch) {
      const slug = componentMatch[1];
      if (!slug) return null;
      const sourceHash = options.getManifest().slugToCurrentSourceHash[slug];
      if (!sourceHash) {
        throw new Error(`Custom component "${slug}" referenced by an artifact is not registered for this page.`);
      }
      return getBlobUrl(sourceHash);
    }
    return null;
  }

  return {
    loadByHash,
    loadBySlug,
    getResolvedByHash: (sourceHash) => resolvedCache.get(sourceHash) ?? null,
    blobUrlToSourceHash,
  };
}

async function fetchArtifactText(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch custom-component artifact at ${url}: HTTP ${res.status}`);
  }
  return res.text();
}
